import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useWorkflowStore from '../stores/useWorkflowStore';
import { getStepFromPathname } from '../constants/flowConfig';
import { loadBkDesignOrderContext } from '../lib/bkDesignOrderContext';
import { hasCachedStep, getResumeMeta } from '../lib/bkSceneCache';
import { resumeStepFromCache } from '../../features/step-checkpoints';

/**
 * Full-scene restore after a page reload.
 *
 * On the first design mount, if the client cache holds the mesh + tool session
 * for the step the URL landed on, rehydrate the workflow resume metadata
 * (stepCompletion / intent) synchronously and install the cached scene. Runs at
 * most once per mount; subsequent in-session navigation is handled by the
 * in-memory step checkpoint sync.
 */
export function useBkSceneResume() {
  const location = useLocation();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;

    const ctx = loadBkDesignOrderContext();
    if (!ctx) return;
    const orderKey = `${ctx.sales_order}:${ctx.customer}`;

    const step = getStepFromPathname(location.pathname);
    if (!step) return;

    // Latch as soon as the order + step resolve, not only on a cache hit. This
    // hook restores the step a *reload* landed on; leaving it armed because the
    // first mount happened to have nothing cached turned every later route change
    // into "reinstall whatever IndexedDB holds for this step", which silently put
    // back the mesh, applied flags and undo snapshots of work the user had just
    // discarded with Previous. In-session navigation belongs to the step
    // checkpoint sync. (The early returns above stay unlatched so a redirect on
    // first paint, or an order context that arrives late, still gets a chance.)
    ranRef.current = true;

    if (!hasCachedStep(orderKey, step)) return;

    // Restore stepper completion + main-process intent so gating/UI match where
    // the user left off (these are otherwise reset to defaults on reload).
    const meta = getResumeMeta(orderKey);
    if (meta) {
      useWorkflowStore.setState((s) => ({
        stepCompletion: { ...s.stepCompletion, ...(meta.stepCompletion || {}) },
        ...(meta.bkMainProcessIntent ? { bkMainProcessIntent: meta.bkMainProcessIntent } : {}),
      }));
    }

    // Own the mesh for this step: stop the raw-scan auto-loader from racing in
    // and overwriting the restored (edited) mesh.
    useWorkflowStore.getState().actions.setScanAutoLoaded(true);

    const ac = new AbortController();
    resumeStepFromCache(orderKey, step, { signal: ac.signal }).catch((e) => {
      if (e?.name !== 'AbortError') console.warn('[useBkSceneResume] resume failed', e);
    });

    return () => ac.abort();
  }, [location.pathname]);
}
